import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { errorMessage } from "../../api/errorMessage";

export type ConversationSummary = {
    id: string; title: string; version: number; turnCount: number; createdAt: string; updatedAt: string;
    scope: { companyId: number; compareCompanyIds: number[]; from: string; to: string };
};
type HistoryPage = { items: ConversationSummary[]; nextOffset: number | null };

export function useAssistantHistory(userId: number | null) {
    const [items, setItems] = useState<ConversationSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [nextOffset, setNextOffset] = useState<number | null>(null);
    const generation = useRef(0);
    const request = useRef<AbortController | null>(null);
    const refresh = useCallback(async (offset = 0) => {
        const epoch = ++generation.current;
        request.current?.abort(); const abort = new AbortController(); request.current = abort;
        setLoading(true); setError("");
        try {
            const page = await api.get<HistoryPage>("/api/assistant/conversations", { offset }, { signal: abort.signal, timeout: 60000 });
            if (epoch !== generation.current || abort.signal.aborted) return;
            const received = page.items.filter(item => typeof item.id === "string" && typeof item.title === "string" && !!item.scope);
            setItems(previous => offset ? [...previous, ...received.filter(item => !previous.some(old => old.id === item.id))] : received);
            setNextOffset(page.nextOffset ?? null);
        } catch (e: any) {
            if (epoch !== generation.current || abort.signal.aborted) return;
            if ([401, 403].includes(e.status)) setItems([]);
            setError(errorMessage(e, "Не удалось загрузить историю."));
        } finally { if (epoch === generation.current) setLoading(false); }
    }, []);
    useEffect(() => {
        setItems([]); setNextOffset(null); setError("");
        if (userId) void refresh();
        const lifecycle = generation;
        return () => { ++lifecycle.current; request.current?.abort(); };
    }, [userId, refresh]);
    function remember(item: ConversationSummary) {
        ++generation.current; request.current?.abort(); setLoading(false);
        setItems(previous => [item, ...previous.filter(old => old.id !== item.id)]);
    }
    function forget(id: string) { setItems(previous => previous.filter(item => item.id !== id)); }
    return { items, loading, error, nextOffset, refresh, remember, forget };
}
